/**
 * Barra de progresso compacta para detalhes de campanha
 * Suporta dois layouts:
 *  - "card" (padrão): bloco em card próprio, ocupa linha inteira
 *  - "inline": faixa enxuta para uso ao lado do título no header da página
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface CampaignProgressBarProps {
  /** Percentual de progresso (0-100) */
  progress: number;
  /** Taxa de sucesso (0-100) */
  successRate: number;
  /** Data/hora de início */
  startedAt: string | null;
  /** Data/hora de conclusão */
  completedAt: string | null;
  /** Tipo de mensagem */
  messageType: string | null;
  /** Variante visual: 'card' (padrão) ou 'inline' (para header) */
  variant?: 'card' | 'inline';
  /** Classe extra para o container */
  className?: string;
}

/**
 * Faixa horizontal compacta de progresso.
 * Variante "inline" remove borda/padding e adapta para uso no header.
 */
export function CampaignProgressBar({
  progress,
  successRate,
  startedAt,
  completedAt,
  messageType,
  variant = 'card',
  className,
}: CampaignProgressBarProps) {
  // Formata datas curtas
  const formatDate = (date: string | null) =>
    date ? format(new Date(date), 'dd/MM HH:mm', { locale: ptBR }) : '—';

  const isInline = variant === 'inline';

  return (
    <div
      className={cn(
        isInline
          ? 'space-y-1.5 w-full'
          : 'rounded-lg border bg-card p-3 space-y-2',
        className,
      )}
    >
      {/* Linha de informações: % + chips de metadados */}
      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs">
        <span className="text-sm font-semibold text-foreground">{progress}%</span>
        <span className="text-muted-foreground">
          Iniciada <span className="text-foreground font-medium">{formatDate(startedAt)}</span>
        </span>
        <span className="text-muted-foreground">
          Concluída <span className="text-foreground font-medium">{formatDate(completedAt)}</span>
        </span>
        <span className="text-muted-foreground">
          Tipo <span className="text-foreground font-medium capitalize">{messageType || 'text'}</span>
        </span>
        <span className="text-muted-foreground ml-auto">
          Sucesso <span className="text-foreground font-medium">{successRate}%</span>
        </span>
      </div>

      {/* Barra horizontal fina, sem gradiente */}
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full bg-primary transition-all duration-500')}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
